import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  FindReplace as DuplicateIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { databaseAPI } from "../services/api.js";

const DuplicateResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // Get duplicate data from navigation state
  const duplicateData = location.state?.duplicateData;
  const duplicateResults = location.state?.duplicateResults;
  const duplicateTaskId = location.state?.duplicateTaskId;

  // Local state
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState(false);

  // Handle different possible data structures from backend
  const initializeDuplicateGroups = () => {
    if (!duplicateResults) return [];

    // Try different possible structures
    if (duplicateResults.duplicate_groups) {
      return duplicateResults.duplicate_groups;
    }
    if (duplicateResults.results?.duplicate_groups) {
      return duplicateResults.results.duplicate_groups;
    }
    if (Array.isArray(duplicateResults)) {
      return duplicateResults;
    }

    return [];
  };

  const [duplicateGroups, setDuplicateGroups] = useState(
    initializeDuplicateGroups()
  );
  const [isDuplicateScanning, setIsDuplicateScanning] = useState(false);
  const [duplicateProgress, setDuplicateProgress] = useState(0);

  // Get dynamic columns from the first record's data
  const getDataColumns = () => {
    if (
      duplicateGroups.length === 0 ||
      !duplicateGroups[0].records ||
      duplicateGroups[0].records.length === 0
    ) {
      return [];
    }

    const firstRecord = duplicateGroups[0].records[0];
    if (!firstRecord.data) {
      return [];
    }

    // Get all keys from the data object, excluding system fields
    return Object.keys(firstRecord.data).filter(
      (key) => !["id", "created_at", "updated_at"].includes(key.toLowerCase())
    );
  };

  const dataColumns = getDataColumns();

  // Initialize expanded groups (expand first few groups by default)
  useEffect(() => {
    if (duplicateGroups.length > 0) {
      const initialExpanded = new Set();
      duplicateGroups.slice(0, 3).forEach((group) => {
        initialExpanded.add(group.id);
      });
      setExpandedGroups(initialExpanded);
    }
  }, [duplicateGroups]);

  // Re-initialize duplicate groups when duplicateResults changes
  useEffect(() => {
    const newGroups = initializeDuplicateGroups();
    setDuplicateGroups(newGroups);
  }, [duplicateResults]);

  // Remove duplicates mutation
  const removeDuplicatesMutation = useMutation({
    mutationFn: (removeData) => databaseAPI.removeDuplicates(removeData),
    onSuccess: (data) => {
      enqueueSnackbar("Selected duplicates removed successfully", {
        variant: "success",
      });
      setSelectedDuplicates([]);
      setConfirmDialog(false);
      // Optionally refresh the duplicate results
      if (data.data?.task_id) {
        pollDuplicateResults(data.data.task_id);
      }
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to remove duplicates",
        { variant: "error" }
      );
      setConfirmDialog(false);
    },
  });

  // Polling function for updated duplicate results
  const pollDuplicateResults = async (taskId) => {
    const maxAttempts = 10;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await databaseAPI.getDuplicateResults(taskId);
        const { state, progress, results, error } = response.data;

        if (state === "PROGRESS") {
          setDuplicateProgress(progress || 0);
          setIsDuplicateScanning(true);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 3000);
          }
        } else if (state === "SUCCESS") {
          setIsDuplicateScanning(false);
          if (results && results.duplicate_groups) {
            setDuplicateGroups(results.duplicate_groups);
            enqueueSnackbar("Duplicate results refreshed", {
              variant: "success",
            });
          }
        } else if (state === "FAILURE") {
          setIsDuplicateScanning(false);
          enqueueSnackbar(error || "Failed to refresh duplicate results", {
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Error polling duplicate results:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        } else {
          setIsDuplicateScanning(false);
          enqueueSnackbar("Failed to refresh duplicate results", {
            variant: "error",
          });
        }
      }
    };

    poll();
  };

  // Handle individual duplicate selection
  const handleDuplicateSelection = (groupId, recordId, selected) => {
    setSelectedDuplicates((prev) => {
      const key = `${groupId}_${recordId}`;
      if (selected) {
        return [...prev, key];
      } else {
        return prev.filter((item) => item !== key);
      }
    });
  };

  // Handle bulk duplicate selection for a group
  const handleBulkDuplicateSelection = (groupId, action) => {
    const group = duplicateGroups.find((g) => g.id === groupId);
    if (!group) return;

    if (action === "select_all") {
      const newSelections = group.records.map(
        (record) => `${groupId}_${record.record_id}`
      );
      setSelectedDuplicates((prev) => [
        ...new Set([...prev, ...newSelections]),
      ]);
    } else if (action === "keep_first") {
      // Select all except the first record
      const recordsToSelect = group.records
        .slice(1)
        .map((record) => `${groupId}_${record.record_id}`);
      setSelectedDuplicates((prev) => [
        ...new Set([...prev, ...recordsToSelect]),
      ]);
    } else if (action === "deselect_all") {
      setSelectedDuplicates((prev) =>
        prev.filter((item) => !item.startsWith(`${groupId}_`))
      );
    }
  };

  // Handle global bulk selection
  const handleGlobalBulkSelection = (action) => {
    if (action === "select_all") {
      const allSelections = [];
      duplicateGroups.forEach((group) => {
        group.records.forEach((record) => {
          allSelections.push(`${group.id}_${record.record_id}`);
        });
      });
      setSelectedDuplicates(allSelections);
    } else if (action === "keep_first_each") {
      const selectionsToKeep = [];
      duplicateGroups.forEach((group) => {
        // Select all except the first record in each group
        group.records.slice(1).forEach((record) => {
          selectionsToKeep.push(`${group.id}_${record.record_id}`);
        });
      });
      setSelectedDuplicates(selectionsToKeep);
    } else if (action === "deselect_all") {
      setSelectedDuplicates([]);
    }
  };

  // Handle remove selected duplicates
  const handleRemoveSelectedDuplicates = () => {
    if (selectedDuplicates.length === 0) {
      enqueueSnackbar("Please select duplicates to remove", {
        variant: "warning",
      });
      return;
    }
    setConfirmDialog(true);
  };

  // Confirm and execute removal
  const confirmRemoval = () => {
    const recordsToRemove = selectedDuplicates
      .map((key) => {
        const parts = key.split("_");
        const groupId = parts[0];
        const recordId = parts.slice(1).join("_");

        const group = duplicateGroups.find((g) => g.id === groupId);
        const record = group?.records.find((r) => r.record_id === recordId);

        if (record) {
          return `${record.database}_${record.table}_${recordId}`;
        }
        return null;
      })
      .filter(Boolean);

    removeDuplicatesMutation.mutate({ records_to_remove: recordsToRemove });
  };

  // Toggle group expansion
  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Handle back to Query Builder
  const handleBackToQueryBuilder = () => {
    navigate("/query-builder");
  };

  // If no duplicate data was passed, redirect back
  if (!duplicateData && !duplicateResults) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          No duplicate data found. Please run duplicate detection first.
        </Alert>
        <Button
          variant="contained"
          startIcon={<BackIcon />}
          onClick={handleBackToQueryBuilder}
        >
          Back to Query Builder
        </Button>
      </Box>
    );
  }

  const totalDuplicates = duplicateGroups.reduce(
    (sum, group) => sum + group.records.length,
    0
  );
  const totalGroups = duplicateGroups.length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBackToQueryBuilder}
          sx={{ mr: 2 }}
        >
          Back to Query Builder
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Duplicate Detection Results
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() =>
            duplicateTaskId && pollDuplicateResults(duplicateTaskId)
          }
          disabled={isDuplicateScanning}
        >
          Refresh Results
        </Button>
      </Box>

      {/* Progress indicator */}
      {isDuplicateScanning && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" gutterBottom>
              Refreshing duplicate results...
            </Typography>
            <LinearProgress variant="determinate" value={duplicateProgress} />
            <Typography variant="caption" color="text.secondary">
              {duplicateProgress}% complete
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detection Summary
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Chip
              icon={<DuplicateIcon />}
              label={`${totalGroups} Duplicate Groups`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<WarningIcon />}
              label={`${totalDuplicates} Total Records`}
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<CheckIcon />}
              label={`${selectedDuplicates.length} Selected for Removal`}
              color="success"
              variant="outlined"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {duplicateGroups.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Bulk Actions
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleGlobalBulkSelection("keep_first_each")}
              >
                Keep First of Each Group
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleGlobalBulkSelection("select_all")}
              >
                Select All Duplicates
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleGlobalBulkSelection("deselect_all")}
              >
                Deselect All
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveSelectedDuplicates}
                disabled={
                  selectedDuplicates.length === 0 ||
                  removeDuplicatesMutation.isLoading
                }
              >
                Remove Selected ({selectedDuplicates.length})
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Duplicate Groups */}
      {duplicateGroups.length > 0 ? (
        <Box sx={{ space: 2 }}>
          {duplicateGroups.map((group, index) => {
            const isExpanded = expandedGroups.has(group.id);
            const groupSelectedCount = selectedDuplicates.filter((item) =>
              item.startsWith(`${group.id}_`)
            ).length;

            return (
              <Accordion
                key={group.id}
                expanded={isExpanded}
                onChange={() => toggleGroupExpansion(group.id)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Badge
                      badgeContent={group.records.length}
                      color="error"
                      sx={{ mr: 2 }}
                    >
                      <DuplicateIcon />
                    </Badge>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Duplicate Group {index + 1}
                    </Typography>
                    <Chip
                      label={`${groupSelectedCount}/${group.records.length} selected`}
                      size="small"
                      color={groupSelectedCount > 0 ? "primary" : "default"}
                      sx={{ mr: 1 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Group Actions */}
                  <Box
                    sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        handleBulkDuplicateSelection(group.id, "keep_first")
                      }
                    >
                      Keep First
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        handleBulkDuplicateSelection(group.id, "select_all")
                      }
                    >
                      Select All for Deletion
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        handleBulkDuplicateSelection(group.id, "deselect_all")
                      }
                    >
                      Deselect All
                    </Button>
                  </Box>

                  {/* Matching Values */}
                  {group.comparison_values && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "info.light",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        color="info.contrastText"
                      >
                        🔗 Matching Values:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {Object.entries(group.comparison_values).map(
                          ([key, value]) => (
                            <Chip
                              key={key}
                              label={`${key}: ${value}`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Records Table */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">Select</TableCell>
                          <TableCell>Source</TableCell>
                          {dataColumns.map((column) => (
                            <TableCell key={column}>
                              {column.charAt(0).toUpperCase() +
                                column.slice(1).replace(/_/g, " ")}
                            </TableCell>
                          ))}
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.records.map((record, recordIndex) => {
                          const isSelected = selectedDuplicates.includes(
                            `${group.id}_${record.record_id}`
                          );
                          const isFirst = recordIndex === 0;

                          return (
                            <TableRow
                              key={`${group.id}_${record.database}_${record.table}_${record.record_id}_${recordIndex}`}
                              sx={{
                                bgcolor: isFirst
                                  ? "success.light"
                                  : isSelected
                                  ? "error.light"
                                  : "inherit",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleDuplicateSelection(
                                      group.id,
                                      record.record_id,
                                      e.target.checked
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={isFirst ? "bold" : "normal"}
                                >
                                  {record.database}.{record.table}
                                  {isFirst && (
                                    <Chip
                                      label="Original"
                                      size="small"
                                      color="success"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Typography>
                              </TableCell>
                              {dataColumns.map((column) => (
                                <TableCell key={column}>
                                  {record.data?.[column] !== undefined &&
                                  record.data?.[column] !== null
                                    ? String(record.data[column])
                                    : "N/A"}
                                </TableCell>
                              ))}
                              <TableCell>
                                <Tooltip
                                  title={
                                    isSelected
                                      ? "Will be deleted"
                                      : "Will be kept"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    color={isSelected ? "error" : "default"}
                                  >
                                    {isSelected ? (
                                      <DeleteIcon />
                                    ) : (
                                      <CheckIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <DuplicateIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Duplicates Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Great! No duplicate records were detected in your data.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Duplicate Removal</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            You are about to permanently remove{" "}
            <strong>{selectedDuplicates.length}</strong> duplicate records from
            your database. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={confirmRemoval}
            variant="contained"
            color="error"
            disabled={removeDuplicatesMutation.isLoading}
            startIcon={
              removeDuplicatesMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {removeDuplicatesMutation.isLoading
              ? "Removing..."
              : "Remove Duplicates"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateResults;
